import { PlaybackConfig } from 'chromecast-caf-receiver/cast.framework';
import { StateController, AppState } from './state-controller';
import { Config } from './config';
import { Logger } from './logger';
import { Hermes } from './network';
import { createDefaultSession } from './types/session';
import { createDefaultMedia } from './types/media';
import {
  USER_CHANNEL_NAMESPACE,
  registerUserChannel,
} from './messages/user-channel';
import {
  DEBUG_CHANNEL_NAMESPACE,
  registerDebugChannel,
} from './messages/debug-channel';
import { registerLoadInterceptor } from './player/load-interceptor';
import { applyResolutionCap, applyStartLowNoCap } from './player/playback-config';
import { registerDrmHandler } from './player/drm-handler';
import { registerCheckpoint } from './player/checkpoint';
import { registerTracksHandler } from './player/tracks-handler';
import { registerMediaStatus } from './player/media-status';
import { registerRenditionReporter } from './player/rendition-reporter';
import { registerEventListeners } from './player/event-listeners';
import { initStartingScreen } from './ui/starting-screen';
import {
  initStandbyScreen,
  fetchAndPreloadHighlights,
  setUserProfile,
} from './ui/standby-screen';
import { injectPlayerOverlayStyles } from './ui/player-overlay-styles';
import { initDatadog } from './monitoring/datadog';

/**
 * Main application class — orchestrates all modules in the correct order
 * and starts the Cast receiver context.
 *
 * **Boot sequence:**
 *   1. Logger + Hermes headers + Datadog + error handlers
 *   2. Kick off highlights fetch (no auth, runs in background)
 *   3. Register player modules + user channel + UI + event listeners
 *   4. Start Cast context
 *   5. Await highlights + first image preload
 *   6. STARTING → IDLE (starting screen fades, standby appears with image ready)
 */
class App {
  private readonly castState: StateController;
  private readonly context = cast.framework.CastReceiverContext.getInstance();
  private readonly playerManager = this.context.getPlayerManager();
  private readonly session = createDefaultSession();
  private readonly media = createDefaultMedia();

  constructor() {
    this.castState = new StateController(AppState.STARTING);

    // -- 1. Logger --
    Logger.init();
    Logger.info('APP', '=== Receiver boot ===');

    // -- 2. Hermes default headers (device info for backend) --
    Hermes.setDefaultHeaders();

    // -- 3. Analytics (Firebase — self-initializes via module side-effect) --
    Logger.info('APP', 'Analytics ready (Firebase side-effect)');

    // -- 3b. Datadog (browser-logs) --
    initDatadog();

    // -- 4. Uncaught error handler --
    this.setupErrorHandlers();

    // -- 5. Kick off highlights fetch early (no auth needed) --
    const highlightsReady = fetchAndPreloadHighlights();

    // -- 6. Player modules --
    this.registerPlayerModules();

    // -- 7. Message channels --
    registerDebugChannel(this.context);
    registerUserChannel(this.context, this.session, () => {
      if (this.session.userId) {
        setUserProfile({
          userId: this.session.userId,
          name: this.session.userName,
          avatarUrl: this.session.userAvatar,
        });
      }
    });

    // -- 8. UI --
    const videoEnded = initStartingScreen();
    initStandbyScreen(this.castState);

    // -- 9. Event listeners (Analytics + Logger for all events) --
    registerEventListeners(this.playerManager, this.context, this.session, this.castState);

    // -- 10. Start context --
    Logger.info('APP', 'Starting Cast context…');
    try {
      this.context.start({
        disableIdleTimeout: Config.DEBUG_MODE,
        playbackConfig: this.buildPlaybackConfig(),
        customNamespaces: {
          [USER_CHANNEL_NAMESPACE]:
            cast.framework.system.MessageType.JSON,
          [DEBUG_CHANNEL_NAMESPACE]:
            cast.framework.system.MessageType.JSON,
        },
      });
    } catch (err) {
      Logger.error('APP', 'context.start() failed', err);
    }

    // -- 10b. Inject custom typography into Cast player overlay --
    injectPlayerOverlayStyles();

    // -- 11. Wait for video + highlights before showing standby --
    Promise.all([highlightsReady, videoEnded])
      .then(() => {
        this.castState.set(AppState.IDLE);
        Logger.info('APP', 'Receiver started', {
          debugMode: Config.DEBUG_MODE,
          buildVersion: Config.BUILD_VERSION,
        });
      })
      .catch((err: unknown) => {
        // Even on failure, transition to IDLE so the receiver is usable
        Logger.error('APP', 'Highlights preload failed, transitioning anyway', err);
        this.castState.set(AppState.IDLE);
      });
  }

  /**
   * Builds the default playback configuration applied to every load.
   *
   * Default: caps the resolution to Full HD (1080p) on all devices — per-load
   * handlers (DRM) inherit and preserve this config. See `applyResolutionCap`.
   *
   * P1 Fase 2 experiment (`EXPERIMENT_START_LOW`, ID-1715): when enabled, drops
   * the cap and starts low instead (see `applyStartLowNoCap`).
   */
  private buildPlaybackConfig(): PlaybackConfig {
    const playbackConfig = new PlaybackConfig();

    if (Config.EXPERIMENT_START_LOW) {
      Logger.warn(
        'APP',
        'EXPERIMENT_START_LOW active — 1080p cap DISABLED, starting at low rendition',
        { initialBandwidth: Config.LOW_START_BANDWIDTH_BPS },
      );
      return applyStartLowNoCap(playbackConfig);
    }

    return applyResolutionCap(playbackConfig);
  }

  private registerPlayerModules(): void {
    registerLoadInterceptor(this.playerManager, this.session, this.media, this.castState);
    registerDrmHandler(this.playerManager, this.session, this.media);
    registerCheckpoint(this.playerManager, this.session, this.media);
    registerTracksHandler(this.playerManager, this.session, this.media);
    registerMediaStatus(this.playerManager, this.session);
    registerRenditionReporter(this.playerManager);

    Logger.info('APP', 'Player modules registered');
  }

  private setupErrorHandlers(): void {
    window.addEventListener('error', (event) => {
      Logger.error('APP', 'Uncaught error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      Logger.error('APP', 'Unhandled rejection', {
        message: reason instanceof Error ? reason.message : String(reason),
        stack: reason instanceof Error ? reason.stack : undefined,
      });
    });
  }
}

try {
  new App();
} catch (err) {
  const msg = err instanceof Error ? err.message : String(err);
  console.error('[APP] Fatal error during initialization:', err);

  const logger = document.getElementById('logger');
  if (logger) {
    const p = document.createElement('p');
    p.className = 'log-error';
    p.textContent = `[FATAL] App init crashed: ${msg}`;
    logger.appendChild(p);
  }
}
